/**
 * TrafficChannelsEditor — Traffic channels with range slider distribution
 *
 * Same visual pattern as WebABVariantEditor:
 * - Paper card per channel with name, range slider, delete
 * - Channel name via Autocomplete freeSolo (select from list or type custom)
 * - Range slider [start, end] on 0-100 scale
 * - Dragging boundary redistributes between neighbors
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box, Typography, Button, IconButton, Paper,
  Slider, TextField, Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { MIN_WEIGHT } from '@modules/placements/utils/weightDistribution';

export interface TrafficChannel {
  channel: string;
  label: string;
  percentage: number;
  priority: number;
}

const CHANNEL_OPTIONS = [
  { value: 'instagram_ads', label: 'Instagram Ads' },
  { value: 'facebook_ads', label: 'Facebook Ads' },
  { value: 'google_ads', label: 'Google Ads' },
  { value: 'tiktok_ads', label: 'TikTok Ads' },
  { value: 'apple_search_ads', label: 'Apple Search Ads' },
  { value: 'youtube_ads', label: 'YouTube Ads' },
  { value: 'influencer', label: 'Influencer Marketing' },
  { value: 'organic_appstore', label: 'Organic App Store' },
  { value: 'organic_playstore', label: 'Organic Play Store' },
  { value: 'organic_landing', label: 'Organic Landing Page' },
  { value: 'referral', label: 'Referral' },
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
  { value: 'push', label: 'Push Notifications' },
  { value: 'other', label: 'Other' },
] as const;

interface TrafficChannelsEditorProps {
  channels: TrafficChannel[];
  onChange: (channels: TrafficChannel[]) => void;
  disabled?: boolean;
}

// ── Calculate cumulative ranges from percentages ──────────────

const calculateRanges = (channels: TrafficChannel[]): Array<[number, number]> => {
  const ranges: Array<[number, number]> = [];
  let start = 0;
  for (const ch of channels) {
    ranges.push([start, start + ch.percentage]);
    start += ch.percentage;
  }
  return ranges;
};

// ── Range slider with local state ─────────────────────────────

const ChannelRangeSlider: React.FC<{
  value: [number, number];
  onCommit: (value: number[]) => void;
  disabled: boolean;
  label: string;
}> = ({ value, onCommit, disabled, label }) => {
  const [local, setLocal] = useState<[number, number]>(value);
  useEffect(() => { setLocal(value); }, [value]);

  return (
    <Slider
      aria-label={label}
      value={local}
      onChange={(_, v) => setLocal(v as [number, number])}
      onChangeCommitted={(_, v) => onCommit(v as number[])}
      min={0}
      max={100}
      disabled={disabled}
      valueLabelDisplay="auto"
      disableSwap
    />
  );
};

// ── Percentage text input with validation on blur ─────────────

const PercentageInput: React.FC<{
  value: number;
  onCommit: (value: number) => void;
  disabled: boolean;
}> = ({ value, onCommit, disabled }) => {
  const [local, setLocal] = useState(String(value));

  useEffect(() => { setLocal(String(value)); }, [value]);

  const handleBlur = () => {
    const num = parseInt(local) || 0;
    onCommit(num); // clamping happens in handler
  };

  return (
    <TextField
      size="small"
      value={local}
      onChange={(e) => setLocal(e.target.value.replace(/[^0-9]/g, ''))}
      onBlur={handleBlur}
      onKeyDown={(e) => { if (e.key === 'Enter') handleBlur(); }}
      disabled={disabled}
      InputProps={{
        endAdornment: <Typography variant="caption" color="text.secondary">%</Typography>,
      }}
      sx={{ width: 70 }}
      inputProps={{ style: { textAlign: 'right' } }}
    />
  );
};

// ── Main component ────────────────────────────────────────────

export function TrafficChannelsEditor({
  channels,
  onChange,
  disabled = false,
}: TrafficChannelsEditorProps) {
  const usedChannels = new Set(channels.map(c => c.channel));
  const availableChannels = CHANNEL_OPTIONS.filter(o => !usedChannels.has(o.value));
  const ranges = useMemo(() => calculateRanges(channels), [channels]);

  // ── Add: take MIN_WEIGHT from first ───────────────────────

  const handleAdd = useCallback(() => {
    const newCh: TrafficChannel = {
      channel: `channel_${Date.now()}`,
      label: '',
      percentage: channels.length > 0 ? MIN_WEIGHT : 100,
      priority: channels.length + 1,
    };

    const updated = channels.length > 0
      ? channels.map((c, i) => ({
          ...c,
          percentage: i === 0 ? c.percentage - MIN_WEIGHT : c.percentage,
        }))
      : [];

    onChange([...updated, newCh]);
  }, [channels, onChange]);

  // ── Remove: freed % goes to first channel ─────────────────

  const handleRemove = useCallback((index: number) => {
    const removed = channels[index];
    const remaining = channels.filter((_, i) => i !== index);
    if (remaining.length === 0) { onChange([]); return; }
    remaining[0] = { ...remaining[0], percentage: remaining[0].percentage + removed.percentage };
    onChange(remaining.map((c, i) => ({ ...c, priority: i + 1 })));
  }, [channels, onChange]);

  // ── Percentage text input — clamp + redistribute below ─────

  const handlePercentageInput = useCallback((index: number, rawValue: number) => {
    if (channels.length <= 1) {
      onChange(channels.map(c => ({ ...c, percentage: 100 })));
      return;
    }

    const updated = channels.map(c => ({ ...c }));

    // Sum of items above (fixed)
    const aboveSum = updated.slice(0, index).reduce((s, c) => s + c.percentage, 0);
    // Items below must keep at least MIN_WEIGHT each
    const belowCount = channels.length - index - 1;
    const maxForThis = 100 - aboveSum - belowCount * MIN_WEIGHT;
    const clamped = Math.max(MIN_WEIGHT, Math.min(rawValue, maxForThis));

    updated[index].percentage = clamped;

    if (belowCount > 0) {
      const remainder = 100 - aboveSum - clamped;
      const base = Math.floor(remainder / belowCount);
      let extra = remainder - base * belowCount;
      for (let i = index + 1; i < updated.length; i++) {
        updated[i].percentage = Math.max(base + (extra-- > 0 ? 1 : 0), MIN_WEIGHT);
      }
    }

    onChange(updated);
  }, [channels, onChange]);

  // ── Range slider (same logic as WebABVariantEditor) ───────

  const handleRangeChange = useCallback((index: number, newRange: number[]) => {
    const [newStart, newEnd] = newRange;
    const currentRange = ranges[index];
    const isFirst = index === 0;
    const isLast = index === channels.length - 1;

    const updated = channels.map(c => ({ ...c }));
    const startChanged = newStart !== currentRange[0];
    const endChanged = newEnd !== currentRange[1];

    if (startChanged && !isFirst) {
      const prevStart = ranges[index - 1][0];
      const clamped = Math.max(prevStart + MIN_WEIGHT, Math.min(newStart, currentRange[1] - MIN_WEIGHT));
      updated[index - 1].percentage = clamped - prevStart;
      updated[index].percentage = currentRange[1] - clamped;
    }

    if (endChanged && !isLast) {
      const nextEnd = ranges[index + 1][1];
      const clamped = Math.max(currentRange[0] + MIN_WEIGHT, Math.min(newEnd, nextEnd - MIN_WEIGHT));
      updated[index].percentage = clamped - currentRange[0];
      updated[index + 1].percentage = nextEnd - clamped;
    }

    if (isFirst && startChanged) return;
    if (isLast && endChanged) return;

    onChange(updated);
  }, [channels, ranges, onChange]);

  // ── Channel name change (Autocomplete freeSolo) ───────────

  const handleChannelSelect = useCallback((index: number, value: string) => {
    if (!value) return;
    const option = CHANNEL_OPTIONS.find(o => o.label === value || o.value === value);
    const updated = [...channels];
    updated[index] = {
      ...updated[index],
      channel: option?.value || value.toLowerCase().replace(/[^a-z0-9]+/g, '_'),
      label: option?.label || value,
    };
    onChange(updated);
  }, [channels, onChange]);

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Traffic Channels</Typography>

      {channels.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No traffic channels configured
        </Typography>
      )}

      {/* Channel cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {channels.map((channel, index) => (
          <Paper
            key={`${channel.channel}-${index}`}
            variant="outlined"
            sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}
          >
            {/* Top row: Channel selector + Delete */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Autocomplete
                freeSolo
                options={[...availableChannels.map(o => o.label), ...(channel.label ? [channel.label] : [])]}
                value={channel.label}
                onChange={(_, value) => handleChannelSelect(index, value as string)}
                onInputChange={(_, value, reason) => {
                  if (reason === 'input') handleChannelSelect(index, value);
                }}
                size="small"
                disabled={disabled}
                sx={{ flex: 1 }}
                renderInput={(params) => (
                  <TextField {...params} label="Channel Name" placeholder="Select or type custom" />
                )}
              />
              <IconButton
                onClick={() => handleRemove(index)}
                disabled={disabled}
                color="error"
                sx={{ ml: 1 }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>

            {/* Slider + percentage input */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1 }}>
              <Box sx={{ flex: 1 }}>
                <ChannelRangeSlider
                  label={`Range for ${channel.label}`}
                  value={ranges[index] ?? [0, 100]}
                  onCommit={(value) => handleRangeChange(index, value)}
                  disabled={disabled}
                />
              </Box>
              <PercentageInput
                value={channel.percentage}
                onCommit={(v) => handlePercentageInput(index, v)}
                disabled={disabled}
              />
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Add Channel */}
      <Button
        startIcon={<AddIcon />}
        onClick={handleAdd}
        disabled={disabled}
        size="small"
        sx={{ mt: 1.5 }}
      >
        Add Channel
      </Button>
    </Box>
  );
}
